<script lang="ts">
  import { page } from '$app/stores';
  import Button from '$components/ui/button.svelte';
  import RangeSlider from '$components/ui/range-slider.svelte';
  import Select from '$components/ui/select/select.svelte';
  import { storyblokEditable } from '$lib/actions/storyblok-editable';
  import { t } from '$lib/i18n';
  import { userDevice } from '$lib/stores/user-device';
  import { isIos } from '$lib/utils/device';
  import { ringSizes, type RingSizeKey } from '$lib/utils/ring';
  import { getSelectedDeviceDimensions, mmToPixels } from '$lib/utils/screen';
  import type { RingSizerStoryblok } from '$types/bloks';

  export let block: RingSizerStoryblok;

  let selectedSize: RingSizeKey = 5;

  $: sizeInMm = ringSizes[selectedSize];
  $: items = $page.data.devices.map((device: { name: string; value: string }) => ({
    label: device.name,
    value: device.name
  }));
  let selectedDevice: string;
  $: if (selectedDevice) {
    const selectedDeviceDimensions = getSelectedDeviceDimensions({
      devices: $page.data.devices,
      selectedDevice
    });
    userDevice.update((prev) => ({
      ...prev,
      ...selectedDeviceDimensions
    }));
  }

  let dismiss = false;
</script>

<!-- Component is only shown on iOS devices -->
{#if isIos()}
  <section class="px-2 py-12 lg:px-8 lg:py-16 md:hidden">
    <div
      class="relative container mx-auto p-5 rounded-sm bg-highlight overflow-hidden"
      use:storyblokEditable={block}
    >
      {#if !dismiss}
        <div
          class="bg-primary/40 absolute top-0 left-0 h-full w-full px-2 flex justify-center items-center"
        >
          <div class="w-full bg-highlight rounded-sm p-3">
            <p class="text-md font-semibold">{$t('ringsizer.modal.title')}</p>
            <p class="text-md text-primary/40 mb-4">{$t('ringsizer.modal.subtitle')}</p>
            <Select
              placeholder={$t('ringsizer.modal.select-label')}
              icon="phone"
              bind:value={selectedDevice}
              {items}
              class="w-full"
              popoverClass="w-[90%]"
              sameWidth={false}
            />
            <Button
              name="confirm"
              disabled={!selectedDevice}
              on:click={() => (dismiss = true)}
              class="mt-4 w-full">{$t('ringsizer.modal.confirm')}</Button
            >
          </div>
        </div>
      {/if}
      <div class="dotted">
        <div class="flex flex-col items-center gap-1 py-1">
          <h1 class="text-2xl font-semibold">
            {block.title}
          </h1>
          <div class="flex flex-col items-center">
            <p class="text-center">
              {block.line_1}
            </p>
            <p class="text-center">
              {block.line_2}
            </p>
          </div>
          <div class="h-[206px] flex items-center justify-center">
            <div
              class="border border-accent rounded-full flex items-center justify-center bg-accent/4 ease-in-out duration-100"
              style={`height: ${mmToPixels(sizeInMm, Number($userDevice.ppi), Number($userDevice.scaleFactor))}px; width: ${mmToPixels(
                sizeInMm,
                Number($userDevice.ppi),
                Number($userDevice.scaleFactor)
              )}px`}
            >
              <p class="text-2xl font-semibold">{selectedSize}</p>
            </div>
          </div>
          <RangeSlider
            min={3}
            max={13.5}
            step={0.5}
            bind:value={selectedSize}
            class="w-full max-w-[327px]"
          />
        </div>
      </div>
    </div>
  </section>
{/if}

<style>
  .dotted {
    background:
      linear-gradient(90deg, hsl(var(--color-grey-1) / 1) calc(22px - 2px), transparent 1%) 2px
        2px / 22px 22px,
      linear-gradient(hsl(var(--color-grey-1) / 1) calc(22px - 2px), transparent 1%) 10px 10px /
        22px 22px,
      hsl(var(--color-grey-12) / 0.14);
  }
</style>
